import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { RagBadge } from "@/components/common/RagBadge";
import { InviteLinkResult } from "@/components/common/InviteLinkResult";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmployeeDialog } from "@/components/workspace/team/EmployeeDialog";
import { PROFILE_STATUS, weeklyHours } from "@/lib/workspace";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";

export function PeopleTab({ orgId, canAdmin, isPeopleAdmin, members }) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [invite, setInvite] = useState(null);
  const load = () => api.get(`/workspace/employees?org_id=${orgId}`).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendInvite = async (p) => {
    try { const { data } = await api.post(`/workspace/employees/${p.id}/invite`); setInvite({ name: p.name, result: data }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const revoke = async (p) => {
    try { await api.post(`/workspace/invitations/${p.open_invitation.id}/revoke`); toast.success("Invitation withdrawn"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  if (!rows) return <Icons.Loader2 className="mx-auto animate-spin text-[#FFB000]" />;
  return (
    <div data-testid="people-tab">
      {canAdmin && (
        <div className="mb-4 flex justify-end">
          <Button onClick={() => setEditing({})} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="add-person-btn">
            <Icons.UserPlus size={16} className="mr-1.5" /> Prepare a profile
          </Button>
        </div>
      )}
      <div className="amden-card divide-y divide-[#EEF1F6]">
        {rows.map((p) => {
          const st = PROFILE_STATUS[p.status] || PROFILE_STATUS.prepared;
          return (
            <div key={p.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center" data-testid={`person-${p.id}`}>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0B1729] text-sm font-bold text-[#FFC24D]">{p.name[0]}</span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-[#0B1729]">{p.name}</span>
                  <RagBadge rag={st.rag} testId={`person-status-${p.id}`}>{st.label}</RagBadge>
                </div>
                <div className="mt-0.5 text-xs text-[#1E2D44]/55">
                  {[p.job_title, p.department, p.manager_name && `reports to ${p.manager_name}`, `${weeklyHours(p.work_pattern)} h/week`].filter(Boolean).join(" · ")}
                </div>
                {p.open_invitation && <div className="mt-0.5 text-[11px] text-[#4A78A8]">Invitation open until {fmtDate(p.open_invitation.expires_at)}</div>}
              </div>
              {canAdmin && (
                <div className="flex flex-wrap gap-2">
                  {!p.user_id && p.status !== "left" && (
                    <Button size="sm" variant="outline" onClick={() => sendInvite(p)} data-testid={`invite-${p.id}`}>
                      <Icons.Send size={14} className="mr-1.5" /> {p.open_invitation ? "Re-send" : "Invite"}
                    </Button>
                  )}
                  {p.open_invitation && <Button size="sm" variant="ghost" className="text-[#C95555]" onClick={() => revoke(p)} data-testid={`revoke-${p.id}`}>Withdraw</Button>}
                  <Button size="sm" variant="ghost" onClick={() => setEditing(p)} data-testid={`edit-person-${p.id}`}><Icons.Pencil size={14} /></Button>
                </div>
              )}
            </div>
          );
        })}
        {rows.length === 0 && <p className="p-8 text-center text-sm text-[#1E2D44]/45">No employee profiles yet.</p>}
      </div>
      {editing && <EmployeeDialog orgId={orgId} profile={editing.id ? editing : null} members={members} isPeopleAdmin={isPeopleAdmin}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {invite && (
        <Dialog open onOpenChange={() => setInvite(null)}>
          <DialogContent className="max-w-lg" data-testid="invite-dialog">
            <DialogHeader>
              <DialogTitle>Invitation ready</DialogTitle>
              <DialogDescription>Single-use link, valid for 7 days. Accepting it links {invite.name} to the profile you prepared.</DialogDescription>
            </DialogHeader>
            <InviteLinkResult name={invite.name} result={invite.result} />
            {!invite.result.email_sent && <p className="text-xs text-[#1E2D44]/50">Team Workspace emails are held back in this environment, so no message was sent.</p>}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
